import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { getProgramTemplates } from "generated/api/program-templates";
import { GetApprovedProgramTemplatesType } from "generated/schemas";

import { useCurrentClient } from "hooks/api/use-clients";
import { programTemplateKeys } from "hooks/api/query-keys";

import type { OptionType } from "ui/select";

/** Matches the `channel` enum in the campaign strategy schema's `audienceCreativeItem` definition (backend `CreativeChannel`). */
const CHANNEL_TEMPLATE_TYPES: Record<string, GetApprovedProgramTemplatesType> = {
  email: GetApprovedProgramTemplatesType.EMAIL,
  sms: GetApprovedProgramTemplatesType.OTHER,
  "web-page": GetApprovedProgramTemplatesType.LANDING_PAGE,
  form: GetApprovedProgramTemplatesType.LANDING_PAGE,
};

interface UseCreativeTemplateOptionsResult {
  templateOptions: OptionType[];
  isLoading: boolean;
}

/** Approved/published program templates for the given creative channel, as `ui/select` options keyed by `templateTouchpointVersionId`. */
export const useCreativeTemplateOptions = (channel: string | undefined): UseCreativeTemplateOptionsResult => {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.id;
  const templateType = channel ? CHANNEL_TEMPLATE_TYPES[channel] : undefined;

  const { data: templates = [], isLoading } = useQuery({
    queryKey: programTemplateKeys.approved(clientId ?? "", templateType ?? ""),
    queryFn: () =>
      getProgramTemplates().getApprovedProgramTemplates({ clientId: clientId! }, { type: templateType! }),
    enabled: Boolean(clientId) && Boolean(templateType),
  });

  const templateOptions = useMemo<OptionType[]>(
    () => templates.map((template) => ({ value: template.versionId, label: template.touchpointName })),
    [templates],
  );

  return { templateOptions, isLoading };
};
